import { apiClient } from '@api'

/**
 * Project Team Service
 *
 * Handles all API calls related to project team member management
 */
const projectTeamService = {
  /**
   * Get all team members for a project
   *
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} Team members with user and role details
   */
  getTeamMembers: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}/team`)
    // Handle both {success: true, data: [...]} and direct array response
    if (response.success || response.data) {
      return response.data || response
    }
    return response
  },

  /**
   * Get team members grouped by role
   *
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Team members grouped by role_slug
   */
  getTeamMembersByRole: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}/team/grouped`)
    return response.data
  },

  /**
   * Get team assignment summary
   *
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Summary with counts and statistics
   */
  getTeamSummary: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}/team/summary`)
    return response.data
  },

  /**
   * Assign a user to a role
   *
   * @param {number} projectId - Project ID
   * @param {number} roleId - Role ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} Assignment details
   */
  assignMember: async (projectId, roleId, userId) => {
    const response = await apiClient.post(`/projects/${projectId}/team/assign`, {
      role_id: roleId,
      user_id: userId,
    })
    return response.data
  },

  /**
   * Remove a user from a role
   *
   * @param {number} projectId - Project ID
   * @param {number} roleId - Role ID
   * @returns {Promise<Object>} Success confirmation
   */
  removeMember: async (projectId, roleId) => {
    const response = await apiClient.delete(`/projects/${projectId}/team/remove/${roleId}`)
    return response.data
  },

  /**
   * Sync multiple team member assignments at once
   *
   * @param {number} projectId - Project ID
   * @param {Array<{role_id: number, user_id: number}>} assignments - Array of assignments
   * @returns {Promise<Object>} Sync results and updated team members
   */
  syncTeamMembers: async (projectId, assignments) => {
    const response = await apiClient.post(`/projects/${projectId}/team/sync`, {
      assignments,
    })
    return response.data
  },

  /**
   * Get users available for assignment to a specific role
   *
   * @param {number} projectId - Project ID
   * @param {number} roleId - Role ID
   * @returns {Promise<Array>} Available users for the role
   */
  getAvailableUsers: async (projectId, roleId) => {
    const response = await apiClient.get(
      `/projects/${projectId}/team/available-users/${roleId}`
    )
    return response.data
  },

  /**
   * Get all roles associated with a project
   *
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} Project roles with assignment info
   */
  getProjectRoles: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}/roles`)
    return response.data
  },

  /**
   * Get executive assigned to a project
   *
   * @param {number} projectId - Project ID
   * @returns {Promise<Object|null>} Executive user data or null
   */
  getExecutive: async (projectId) => {
    try {
      const response = await projectTeamService.getTeamMembersByRole(projectId)
      return response.executive?.members?.[0] || null
    } catch (error) {
      console.error('Failed to get executive:', error)
      return null
    }
  },

  /**
   * Get project manager assigned to a project
   *
   * @param {number} projectId - Project ID
   * @returns {Promise<Object|null>} PM user data or null
   */
  getProjectManager: async (projectId) => {
    try {
      const response = await projectTeamService.getTeamMembersByRole(projectId)
      return response['project-manager']?.members?.[0] || null
    } catch (error) {
      console.error('Failed to get project manager:', error)
      return null
    }
  },
}

export default projectTeamService
